import { useRef } from 'react';
import Dropdown from './Dropdown';
import DropdownItem from './DropdownItem';
import StatelessSelect from './StatelessSelect';
import { SpotSearchbar } from '@enterprise/spot';
import { PresetEntity } from '../../../../services/PresetService';
import { observer } from 'mobx-react';
import i18n from 'i18next';
import { Trans } from 'react-i18next';
import { LookupClassification } from '../../../../core/models/datasource/lookups/parts';
import { GetItemQuery, InvoiceItem } from '../../../../core/models';

const NO_FILTER_SELECTED = null;

interface PageProps {
    classifications: LookupClassification[];
    submit: any;
    currentFilter: any;
    updateFilter: any;
    selectedPresetId?: any;
    applyPreset?: any;
    presets?: PresetEntity[];
    viewAllPresets?: any;
    searchItemsForAutoComplete?: (query: GetItemQuery) => Promise<InvoiceItem[]>;
}

const ItemSearchBar = observer(function ItemSearchBarComponent(props: PageProps) {
    const { classifications = [], presets = [], currentFilter, updateFilter, selectedPresetId, applyPreset, viewAllPresets } = props;

    const classesDropRef = useRef<any>(null);
    const savedSearchesDropdownRef = useRef<any>(null);

    const showPresets = () => {
        savedSearchesDropdownRef.current.handleToggleClick();
    };

    const submit = (value) => {
        savedSearchesDropdownRef.current.close();
        props.submit(value);
    };

    const createSearchTypeDropdown = () => {
        const updateSearchType = (searchType) => updateFilter({ searchType });

        return (
            <Dropdown
                isSearchBarDropdown={true}
                title={i18n.t('itemSearchBar:searchType', 'Search Type')}
                value={currentFilter.searchType}
                style={{ width: '150px' }}
                isSameValueSelectionClearsValue={false}
                onChanged={updateSearchType}
            >
                <DropdownItem label={i18n.t('itemSearchBar:id', 'ID')} value={'id'} key={1}>
                    <span>
                        <Trans i18nKey="itemSearchBar:id">ID</Trans>
                    </span>
                </DropdownItem>
                <DropdownItem label={i18n.t('itemSearchBar:description', 'Description')} value={'description'} key={2}>
                    <span>
                        <Trans i18nKey="itemSearchBar:description">Description</Trans>
                    </span>
                </DropdownItem>
            </Dropdown>
        );
    };

    const createClassesDropdown = () => {
        const currentClass = classifications.find((classification) => classification.pimsGeneratedId === `${currentFilter.classId}`);
        const currentSubclass = currentClass
            ? currentClass.subClassifications?.find((subclass) => subclass.pimsGeneratedId === `${currentFilter.subclassId}`)
            : undefined;

        if (classesDropRef.current) {
            let label = i18n.t('itemSearchBar:class', 'Class');
            if (currentClass) {
                label = currentSubclass ? `${currentClass.description} / ${currentSubclass.description}` : currentClass.description;
            }
            classesDropRef.current.setLabel(label);
        }

        const setClassId = (classId) => {
            const classification = classifications.find(({ pimsGeneratedId }) => pimsGeneratedId === classId);
            updateFilter({ classId, className: classification?.name, subclassId: null, subclassName: null });
        };

        const setSubclassId = (subclassId) => {
            const subClassification = currentClass?.subClassifications?.find(({ pimsGeneratedId }) => pimsGeneratedId === subclassId);
            updateFilter({ subclassId, subclassName: subClassification?.name });
        };

        return (
            <Dropdown
                isSearchBarDropdown={true}
                title={i18n.t('itemSearchBar:class', 'Class')}
                isSelfControlled={true}
                style={{ width: '230px' }}
                ref={(r) => {
                    classesDropRef.current = r;
                }}
                contentWidth="400px"
                isAlignContentLeft={false}
                data-automation-id={'class-filter-dropdown'}
            >
                <DropdownItem>
                    <div>
                        <StatelessSelect
                            key="classId"
                            handler={setClassId}
                            placeholder={i18n.t('itemSearchBar:selectClass', 'Select a class')}
                            placeholderValue={NO_FILTER_SELECTED}
                            options={classifications}
                            valueFieldName="pimsGeneratedId"
                            displayFieldName="description"
                            value={currentFilter.classId}
                        />
                        {currentFilter.classId && (
                            <StatelessSelect
                                key="subclassId"
                                handler={setSubclassId}
                                placeholder={i18n.t('itemSearchBar:selectSubclass', 'Select a subclass')}
                                placeholderValue={NO_FILTER_SELECTED}
                                options={currentClass?.subClassifications || []}
                                valueFieldName="pimsGeneratedId"
                                displayFieldName="description"
                                value={currentFilter.subclassId}
                            />
                        )}
                    </div>
                </DropdownItem>
            </Dropdown>
        );
    };

    const createSavedSearchesDropdown = () => {
        return (
            <Dropdown
                value={selectedPresetId}
                style={{ width: '150px' }}
                onChanged={applyPreset}
                isShowContentOnly={true}
                ref={(r) => {
                    savedSearchesDropdownRef.current = r;
                }}
                contentWidth="300px"
                contentStyle={{ marginTop: '-25px', marginLeft: '25px' }}
            >
                <DropdownItem key="01" isHeading={true}>
                    <div className="spot-typography__font-weight--bold">
                        <Trans i18nKey="itemSearchBar:savedSearches">Saved Searches</Trans>
                        <a href="#" className="spot-link" style={{ marginLeft: '15px' }} onClick={viewAllPresets}>
                            <Trans i18nKey="itemSearchBar:viewAll">View All</Trans>
                        </a>
                    </div>
                </DropdownItem>
                {Boolean(presets?.length) && <DropdownItem key={'divider'} isDivider={true} />}
                {presets?.map((preset) => {
                    return (
                        <DropdownItem label={preset.name} value={preset.id} key={preset.id}>
                            <span>{preset.name}</span>
                        </DropdownItem>
                    );
                })}
            </Dropdown>
        );
    };

    const updateSearchValue = (searchValue) => updateFilter({ searchValue });

    return (
        <>
            <SpotSearchbar
                isSelfControlled={false}
                value={currentFilter.searchValue}
                onChange={updateSearchValue}
                onClick={showPresets}
                onSubmit={submit}
            >
                {createSearchTypeDropdown()}
                {createClassesDropdown()}
            </SpotSearchbar>
            {createSavedSearchesDropdown()}
        </>
    );
});

export default ItemSearchBar;
